import axios from 'axios';
import { getApiUrl, parseMgkResponse } from '@/config/api';
import authService from '@/services/auth';

const BASE = '/api/ProyectoGestion';

function authHeaders() {
  const token = authService.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function tokenArchivo(archivo) {
  if (!archivo || typeof archivo !== 'object') return '';
  return archivo._TokenSecreto || archivo._tokenSecreto || archivo.TokenSecreto || archivo.tokenSecreto || '';
}

function urlDescargaArchivo(archivo) {
  const archivoId = typeof archivo === 'object' ? archivo.ProyectoArchivoId : archivo;
  const token = tokenArchivo(archivo);
  return getApiUrl(`${BASE}/archivos/descargar/${archivoId}/${encodeURIComponent(token)}`);
}

function nombreDesdeContentDisposition(contentDisposition) {
  if (!contentDisposition) return null;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || null;
}

function guardarBlob(blob, nombreArchivo) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombreArchivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function abrirBlob(blob) {
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener');
  if (!win) {
    window.URL.revokeObjectURL(url);
    throw new Error('El navegador bloqueo la ventana emergente');
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

function esVisualizable(archivo) {
  if (!archivo || typeof archivo !== 'object') return false;
  const mime = String(archivo.MimeType || '').toLowerCase();
  const nombre = String(archivo.NombreOriginal || '').toLowerCase();
  return mime === 'application/pdf'
    || mime.startsWith('image/')
    || /\.(pdf|png|jpe?g|gif|webp|bmp|svg)$/i.test(nombre);
}

export default {
  async listarContactos(proyectoClave) {
    const response = await axios.get(getApiUrl(`${BASE}/contactos?proyectoClave=${encodeURIComponent(proyectoClave)}`));
    return Array.isArray(response.data) ? response.data : [];
  },

  async guardarContacto(model) {
    const response = await axios.post(getApiUrl(`${BASE}/contactos/guardar`), model);
    return parseMgkResponse(response);
  },

  async borrarContacto(item) {
    const response = await axios.post(getApiUrl(`${BASE}/contactos/borrar`), item);
    return parseMgkResponse(response);
  },

  async listarArchivos(proyectoClave) {
    const response = await axios.get(getApiUrl(`${BASE}/archivos?proyectoClave=${encodeURIComponent(proyectoClave)}`));
    return Array.isArray(response.data) ? response.data : [];
  },

  async subirArchivo(archivo, proyectoClave, opciones = {}) {
    const form = new FormData();
    form.append('archivo', archivo);
    form.append('proyectoClave', proyectoClave);
    if (opciones.categoria != null) form.append('categoria', opciones.categoria);
    if (opciones.observaciones != null) form.append('observaciones', opciones.observaciones);
    if (opciones.esPrincipal != null) form.append('esPrincipal', opciones.esPrincipal);

    const response = await axios.post(getApiUrl(`${BASE}/archivos/subir`), form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async borrarArchivo(archivo) {
    const token = tokenArchivo(archivo);
    const response = await axios.post(
      getApiUrl(`${BASE}/archivos/borrar?token=${encodeURIComponent(token)}`),
      { ProyectoArchivoId: archivo.ProyectoArchivoId }
    );
    return parseMgkResponse(response);
  },

  async descargarArchivo(archivo) {
    const response = await axios.get(urlDescargaArchivo(archivo), {
      responseType: 'blob',
      headers: authHeaders()
    });
    const contentType = response.headers?.['content-type'] || 'application/octet-stream';
    const nombreHeader = nombreDesdeContentDisposition(response.headers?.['content-disposition']);
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });
    guardarBlob(blob, nombreHeader || archivo.NombreOriginal || `proyecto-archivo-${archivo.ProyectoArchivoId}`);
    return response.data;
  },

  async visualizarArchivo(archivo) {
    const response = await axios.get(urlDescargaArchivo(archivo), {
      responseType: 'blob',
      headers: authHeaders()
    });
    const contentType = response.headers?.['content-type'] || archivo?.MimeType || 'application/octet-stream';
    const blob = response.data instanceof Blob
      ? new Blob([response.data], { type: contentType })
      : new Blob([response.data], { type: contentType });
    abrirBlob(blob);
    return response.data;
  },

  esVisualizable,

  urlDescarga(archivo) {
    return urlDescargaArchivo(archivo);
  }
};
